/**
 * Captures a DOM element and saves it as a paginated PDF.
 *
 * @param {HTMLElement} element        - Element to capture
 * @param {string}      filename       - Output filename (without .pdf)
 * @param {object}      [options]
 * @param {"portrait"|"landscape"} [options.orientation="portrait"]
 * @param {HTMLElement} [options.scrollContainer]  - The element that actually scrolls (defaults to element)
 * @param {Function}    [options.onclone]          - html2canvas onclone callback (e.g. for Chart.js fix)
 */
export async function exportElementToPdf(element, filename, options = {}) {
  const { jsPDF } = await import("jspdf");
  const { default: html2canvas } = await import("html2canvas");

  const {
    orientation = "portrait",
    scrollContainer = element,
    onclone,
  } = options;

  const prevScrollTop = scrollContainer.scrollTop;
  scrollContainer.scrollTop = 0;

  const doc = new jsPDF({ orientation, unit: "px", format: "a4" });
  const pageWidth = doc.internal.pageSize.width - 30;
  const pageHeight = doc.internal.pageSize.height - 30;
  const captureHeight = element.scrollHeight;

  // Measure every table row's position relative to the element's top edge.
  // Done before html2canvas so the DOM is still laid out correctly.
  const elementTop = element.getBoundingClientRect().top;
  const rows = Array.from(element.querySelectorAll("tr")).map((row) => {
    const rect = row.getBoundingClientRect();
    return {
      top: rect.top - elementTop,
      bottom: rect.bottom - elementTop,
    };
  });

  try {
    const canvas = await html2canvas(element, {
      ignoreElements: (el) => el.classList.contains("pdf-overlay"),
      useCORS: true,
      scale: 1,
      height: captureHeight,
      scrollX: 0,
      scrollY: 0,
      ...(onclone ? { onclone } : {}),
    });

    // Max canvas pixels that fit on one PDF page.
    const maxSlicePx = Math.floor(pageHeight * (canvas.width / pageWidth));

    let y = 0;
    while (y < canvas.height) {
      const naturalEnd = Math.min(y + maxSlicePx, canvas.height);
      let sliceEnd = naturalEnd;

      // If this isn't the last page, find a row whose body straddles the
      // natural cut point and move the cut up to just before that row starts.
      // Fall back to the natural cut only when a single row is taller than a
      // full page (nothing we can do about that).
      if (sliceEnd < canvas.height) {
        const straddling = rows.find(
          (r) => r.top < naturalEnd && r.bottom > naturalEnd
        );
        if (straddling && straddling.top > y) {
          sliceEnd = Math.floor(straddling.top);
        }
      }

      const currentSlice = sliceEnd - y;
      const pageCanvas = document.createElement("canvas");
      pageCanvas.width = canvas.width;
      pageCanvas.height = currentSlice;
      pageCanvas
        .getContext("2d")
        .drawImage(
          canvas,
          0,
          y,
          canvas.width,
          currentSlice,
          0,
          0,
          canvas.width,
          currentSlice
        );

      const renderedHeight = currentSlice * (pageWidth / canvas.width);
      doc.addImage(
        pageCanvas.toDataURL("image/png"),
        "PNG",
        15,
        15,
        pageWidth,
        renderedHeight
      );
      y = sliceEnd;
      if (y < canvas.height) doc.addPage();
    }

    doc.save(`${filename}.pdf`);
  } finally {
    scrollContainer.scrollTop = prevScrollTop;
  }
}
